import React, {useState, useEffect} from 'react'

import { useLocation } from 'react-router-dom'
import { Table, Chip } from '@arnsbogroup/react-voicearchive'
import Icon from 'components/Icon'
import { useIntl, FormattedDate } from 'react-intl'
import SearchPage from 'containers/SearchPage'
import classNames from 'classnames'

const SupplierSearchPage = ( { history, ...props } ) => {
    const [selectedSupplierTab, setSelectedSupplierTab] = useState('overview')

    const location = useLocation()
    useEffect(() => {
        const parts = location.pathname.split('/')
        if (parts[parts.length - 3] == 'suppliers') {
            setSelectedSupplierTab(parts[parts.length - 1])
        }
    }, [location])


    const Span = ({ children }) => <span>{ children }</span>

    const SupplierStatus = ({ status }) => (
            <Chip size="xs" state={{
                'BLOCKED': 'error',
                'INACTIVE': 'error',
                'ACTIVE': 'success',
                'APPLICANT': 'warning',
                'ONLINE': 'success',
                'IN REVIEW': 'warning',
                'ACCEPTED': 'warning',
                'ONBOARDING': 'warning',
                'REJECTED': 'error',
            }[status] ?? undefined}>
            <span className="capitalize">
                { status ? status.toLowerCase() : '' }
            </span>
        </Chip>
    )

    const RateAmount = (rate, currency) =>
        <Span>
            {{
                "EUR" : "€",
                "USD" : "$",
                "GDP" : "£"
            }[currency] ?? currency}
            { parseFloat(rate).toFixed(0) }
        </Span>

    const Gender = ({ gender }) => {
        const i = parseInt(gender) - 1
        return (
            <span className="text-center text-sm">
                <Icon
                    className={['text-blue-600', 'text-red-600', 'text-purple-600'][i]}
                    icon={['mars', 'venus', 'genderless'][i]}
                />
            </span>
        )
    }

    const StandardRates = ({ priorities = [] }) => priorities.find(({ slug }) => slug === 'voicestar') ? (
        <Icon
            className="text-sm text-primary"
            icon="star"
        />
    ) : null

    const CustomRates = ({ priorities = [] }) => priorities.find(({ slug }) => slug === 'custom_rates') ? (
        <Icon
            className="text-sm text-purple-600"
            icon="funnel-dollar"
        />
    ) : null

    const Availability = ({ availability = [] }) => {
        const intl = useIntl();
        const { icon, iconClassName } = unavailabilityProps(availability)
        const message = (availability.length > 0) ? availability[0].message : '';
        const startDate = (availability.length > 0) ? new Date(availability[0].dateStart * 1000) : '';
        const endDate = (availability.length > 0) ? new Date(availability[0].dateEnd * 1000) : '';
        const dateRange = (startDate && endDate) ? `${intl.formatDate(startDate)} ${intl.formatTime(startDate)} - ${intl.formatDate(endDate)} ${intl.formatTime(endDate)}` : '';
        return (
            <Icon
                className={iconClassName}
                icon={icon}
                title={message + ' ' + dateRange}
            />
        )
    }

    const unavailabilityProps = (unavailabilities = []) => {
        let state = []
        let current = []
        let next24Hours = []
        let future = []

        unavailabilities.map((unavailability) => {
            const { dateStart, dateEnd } = unavailability
            const start = new Date(dateStart * 1000)
            const end = new Date(dateEnd * 1000)
            const now = new Date()
        
            if (start < now && end > now) {
                current.push(unavailability)
            } else if (start > now && (Math.abs(end.getTime() - now.getTime()) / (60 * 60 * 1000)) < 24) {
                next24Hours.push(unavailability)
            } else if (start > now) {
                future.push(unavailability)
            }
        
            if (current.length) {
                state = ['text-warning', 'exclamation-circle', 'bg-warning']
            } else if (next24Hours.length) {
                state = ['text-negative', 'ban', 'bg-negative']
            } else if (future.length) {
                state = ['text-blue-600', 'info-circle', 'bg-blue-600']
            } else {
                state = ['text-positive', 'check-circle', '']
            }
        })

        const [iconClassName, icon, className] = state
        return { icon, iconClassName, className }
    }

    return(
        <SearchPage {...props}
            baseApiUrl="/rest/v2/supplier"
            indicators={[
                Gender,
                StandardRates,
                CustomRates,
                Availability,
            ]}
            components={{
                Tr: ({ childProps = {}, ...innerProps }) => {
                    const { className } = unavailabilityProps(childProps.unavailability)
                    return (
                    <Table.Tr
                        {...innerProps }
                        onClick={childProps.uid ? () => history.push(`/suppliers/${childProps.uid}/${selectedSupplierTab}`) : undefined}
                        title={{
                            BLOCKED: 'Blocked'
                        }[childProps.status] ?? undefined}
                        className={classNames(className, {
                            'cursor-pointer': !!childProps,
                            'bg-blue-50': props?.location?.pathname.startsWith(`/suppliers/${childProps?.uid}`),
                        })}
                    />
                    )
                },
                Td: ({ childProps = {}, ...props }) => (
                    <Table.Td
                    {...props }
                    className={{
                        'BLOCKED': 'line-through opacity-50',
                        'INACTIVE': 'opacity-50',
                    }[childProps.status] ?? undefined}
                    />
                )
            }}
            head={{
                userid: { sortable: true, className: 'w-52', label: 'ID' },
                types: { sortable: true, label: 'Roles' },
                languages: { className: 'w-60' },
                demos: { className: 'w-12', label: <span title="# of demos"><Icon icon="microphone" /></span> },
                rate_commercial: { sortable: true, className: 'w-20', label: <Icon title="Commercial Base Rate" icon="microphone" /> },
                rate_video_presentation: { sortable: true, className: 'w-20', label: <Icon title="Video Presentation Base Rate" icon="video" /> },
                local_time: { className: 'w-20', label: <Icon title="Local Time" icon="clock" /> },
                country: { sortable: true, className: 'w-16', label: <Icon title="Country" icon="flag" /> },
                city: { sortable: true, className: 'w-16', label: 'City' },
                status_changed_at: { className: 'w-24', label: <span title="Last status changed at">Status <Icon icon="clock" /></span> },
                commentsinternal: (_, { notes }) => ({ className: notes?.length ? 'w-80' : 'w-20', label:<Icon icon="comment" /> }),
                firstname: { sortable: true },
                lastname: { sortable: true },
                email: { sortable: true },
            }}
            body={!props.match.isExact ? {
                userid: Span,
            } : {
                status: SupplierStatus,
                userid: Span,
                types: ({ types = [] }) => (
                    <span>{ types.map(({ shortname }) => shortname).join(', ') }</span>
                ),
                languages: ({ languages = [] }) => (
                    <span>{ languages.map(({ name }) => name).join(', ') }</span>
                ),
                rate_commercial: ({ rate_commercial = 0, rate_commercial_currency = 'EUR' }) =>  RateAmount(rate_commercial, rate_commercial_currency),
                rate_video_presentation: ({ rate_video_presentation = 0, rate_video_presentation_currency = 'EUR' }) =>  RateAmount(rate_video_presentation, rate_video_presentation_currency),
                firstname: Span,
                lastname: Span,
                country: ({ country }) => <Span>{ country?.iso }</Span>,
                city: Span,
                email: ({ email }) => <a href={`mailto:${email}`} target="_blank" className="text-primary">{ email }</a>,
                demos: ({ demos }) => <Span>{ demos.length }</Span>,
                local_time: Span,
                status_changed_at: ({ status_changed_at }) => (
                    <FormattedDate value={new Date(status_changed_at * 1000)} />
                ),
                commentsinternal: ({ commentsinternal }, { notes }) => {
                    if (!notes) {
                        return null
                    }

                    const [before, after] = commentsinternal.toLowerCase().split(notes.toLowerCase())

                    if (!after) {
                        null
                    }

                    return (
                    <span>
                        <span>{ before }</span>
                        <span className="font-bold bg-yellow-400">{ notes }</span>
                        <span>{ after }</span>
                    </span>
                    )
                },
            }
            }
        />
    )
}

export default SupplierSearchPage
